import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";

const BookReviewIntl = () => {
  const navigate = useNavigate();

  const [reviewYear, setReviewYear] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [numberOfPages, setNumberOfPages] = useState("");
  const [publisher, setPublisher] = useState("");
  const [country, setCountry] = useState("");

  // id of the record currently being edited (null = adding a new record)
  const [editingId, setEditingId] = useState(null);

  const [bookReviewList, setBookReviewList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const token = localStorage.getItem("token");

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const clearForm = () => {
    setReviewYear("");
    setBookTitle("");
    setNumberOfPages("");
    setPublisher("");
    setCountry("");
    setEditingId(null);
  };

  const refreshList = async () => {
    const refreshedData = await axios.get(
      `${BACKEND_URL}/api/book_review_international`,
      authHeaders
    );

    setBookReviewList(
      Array.isArray(refreshedData.data) ? refreshedData.data : []
    );
  };

  // =========================================================
  // Load previously saved International Book Review records
  // =========================================================
  useEffect(() => {
    const fetchBookReviews = async () => {
      try {
        if (!token) {
          navigate("/login");
          return;
        }

        const response = await axios.get(
          `${BACKEND_URL}/api/book_review_international`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setBookReviewList(
          Array.isArray(response.data) ? response.data : []
        );
      } catch (error) {
        console.error("Error loading International Book Reviews:", error);

        toast.error(
          error.response?.data?.message ||
            "Failed to load International Book Review data."
        );
      } finally {
        setFetching(false);
      }
    };

    fetchBookReviews();
  }, [token, navigate]);

  // =========================================================
  // Start editing a record (fills the form)
  // =========================================================
  const handleEdit = (item) => {
    setEditingId(item.id);
    setReviewYear(
      item.review_year !== null && item.review_year !== undefined
        ? String(item.review_year)
        : ""
    );
    setBookTitle(item.book_title || "");
    setNumberOfPages(
      item.number_of_pages !== null && item.number_of_pages !== undefined
        ? String(item.number_of_pages)
        : ""
    );
    setPublisher(item.publisher || "");
    setCountry(item.country || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================================================
  // Save (add new) or Update (edit) International Book Review
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!reviewYear) {
      toast.warning("Please enter the review year.");
      return;
    }

    if (!bookTitle.trim()) {
      toast.warning("Please enter the book title.");
      return;
    }

    if (numberOfPages === "" || Number(numberOfPages) <= 0) {
      toast.warning("Please enter a valid number of pages.");
      return;
    }

    if (!publisher.trim()) {
      toast.warning("Please enter the publisher.");
      return;
    }

    if (!country.trim()) {
      toast.warning("Please enter the country.");
      return;
    }

    const payload = {
      review_year: Number(reviewYear),
      book_title: bookTitle.trim(),
      number_of_pages: Number(numberOfPages),
      publisher: publisher.trim(),
      country: country.trim(),
    };

    try {
      setLoading(true);

      if (editingId !== null) {
        await axios.put(
          `${BACKEND_URL}/api/book_review_international/${editingId}`,
          payload,
          authHeaders
        );

        toast.success("International Book Review updated successfully!");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/book_review_international`,
          payload,
          authHeaders
        );

        toast.success("International Book Review saved successfully!");
      }

      clearForm();

      // Refresh saved records
      await refreshList();
    } catch (error) {
      console.error("Error saving International Book Review:", error);

      toast.error(
        error.response?.data?.message ||
          (editingId !== null
            ? "Failed to update International Book Review."
            : "Failed to save International Book Review.")
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Delete International Book Review
  // =========================================================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/book_review_international/${id}`,
        authHeaders
      );

      toast.success("International Book Review deleted successfully!");

      setBookReviewList((prev) => prev.filter((item) => item.id !== id));

      // If the deleted record was being edited, reset the form
      if (editingId === id) {
        clearForm();
      }
    } catch (error) {
      console.error("Error deleting International Book Review:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to delete International Book Review."
      );
    }
  };

  if (fetching) {
    return <div style={styles.loading}>Loading...</div>;
  }

  const isEditing = editingId !== null;

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" autoClose={3000} />

      <div style={styles.card}>
        <h1 style={styles.title}>International Book Review</h1>

        <p style={styles.description}>
          {isEditing
            ? "Editing the selected international book review."
            : "Enter the details of the international book review."}
        </p>

        {/* =================================================
            ADD / EDIT INTERNATIONAL BOOK REVIEW FORM
        ================================================== */}
        <form onSubmit={handleSubmit}>
          {/* Review Year */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Review Year</label>

            <input
              type="number"
              min="1900"
              max="2100"
              value={reviewYear}
              onChange={(e) => setReviewYear(e.target.value)}
              placeholder="Enter review year"
              style={styles.input}
            />
          </div>

          {/* Book Title */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Book Title</label>

            <input
              type="text"
              value={bookTitle}
              onChange={(e) => setBookTitle(e.target.value)}
              placeholder="Enter book title"
              style={styles.input}
            />
          </div>

          {/* Number of Pages */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Number of Pages</label>

            <input
              type="number"
              min="1"
              value={numberOfPages}
              onChange={(e) => setNumberOfPages(e.target.value)}
              placeholder="Enter number of pages"
              style={styles.input}
            />
          </div>

          {/* Publisher */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Publisher</label>

            <input
              type="text"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              placeholder="Enter publisher"
              style={styles.input}
            />
          </div>

          {/* Country */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Country</label>

            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Enter country"
              style={styles.input}
            />
          </div>

          {/* Buttons */}
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={styles.backButton}
            >
              Back
            </button>

            <div style={styles.rightButtons}>
              {isEditing && (
                <button
                  type="button"
                  onClick={clearForm}
                  style={styles.cancelButton}
                >
                  Cancel Edit
                </button>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.saveButton,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading
                  ? isEditing
                    ? "Updating..."
                    : "Saving..."
                  : isEditing
                  ? "Update"
                  : "Save"}
              </button>
            </div>
          </div>
        </form>

        {/* =================================================
            SAVED RECORDS
        ================================================== */}
        <div style={styles.savedSection}>
          <h2 style={styles.savedTitle}>Added International Book Reviews</h2>

          {bookReviewList.length === 0 ? (
            <div style={styles.noData}>
              No international book review has been added yet.
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Review Year</th>

                    <th style={styles.th}>Book Title</th>

                    <th style={styles.th}>Number of Pages</th>

                    <th style={styles.th}>Publisher</th>

                    <th style={styles.th}>Country</th>

                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {bookReviewList.map((item) => (
                    <tr
                      key={item.id}
                      style={
                        item.id === editingId ? styles.editingRow : undefined
                      }
                    >
                      <td style={styles.td}>{item.review_year}</td>

                      <td style={styles.td}>{item.book_title}</td>

                      <td style={styles.td}>{item.number_of_pages}</td>

                      <td style={styles.td}>{item.publisher}</td>

                      <td style={styles.td}>{item.country}</td>

                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          style={styles.editButton}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          style={styles.deleteButton}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f4f6fb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "30px",
    boxSizing: "border-box",
  },

  card: {
    width: "100%",
    maxWidth: "1200px",
    backgroundColor: "white",
    padding: "35px",
    borderRadius: "12px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
  },

  title: {
    color: "#542e88",
    textAlign: "center",
    marginBottom: "10px",
  },

  description: {
    textAlign: "center",
    color: "#666",
    marginBottom: "30px",
  },

  formGroup: {
    marginBottom: "25px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "bold",
    color: "#333",
  },

  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontSize: "16px",
    boxSizing: "border-box",
    outline: "none",
  },

  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "25px",
  },

  rightButtons: {
    display: "flex",
    gap: "10px",
  },

  backButton: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#777",
    color: "white",
    cursor: "pointer",
  },

  cancelButton: {
    padding: "10px 20px",
    border: "1px solid #542e88",
    borderRadius: "6px",
    backgroundColor: "white",
    color: "#542e88",
    cursor: "pointer",
    fontWeight: "bold",
  },

  saveButton: {
    padding: "10px 25px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#542e88",
    color: "white",
    cursor: "pointer",
    fontWeight: "bold",
  },

  savedSection: {
    marginTop: "40px",
  },

  savedTitle: {
    color: "#542e88",
    marginBottom: "15px",
  },

  noData: {
    padding: "20px",
    textAlign: "center",
    color: "#777",
    backgroundColor: "#f8fafc",
    border: "1px solid #ddd",
    borderRadius: "6px",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #ddd",
  },

  th: {
    backgroundColor: "#542e88",
    color: "white",
    padding: "12px",
    textAlign: "left",
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px",
    border: "1px solid #ddd",
    color: "#333",
    whiteSpace: "nowrap",
  },

  editingRow: {
    backgroundColor: "#f3e8ff",
  },

  editButton: {
    padding: "7px 12px",
    border: "none",
    borderRadius: "5px",
    backgroundColor: "#2563eb",
    color: "white",
    cursor: "pointer",
    marginRight: "8px",
  },

  deleteButton: {
    padding: "7px 12px",
    border: "none",
    borderRadius: "5px",
    backgroundColor: "#dc2626",
    color: "white",
    cursor: "pointer",
  },

  loading: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "20px",
  },
};

export default BookReviewIntl;
